import React from 'react';
import { Table, Pagination, Spinner } from 'react-bootstrap';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { getAgeCategoriesDisplay } from '../api/tournament/manageTournamentService';
import { PermissionWrapper } from '../components/PermissionWrapper/PermissionWrapper';

interface TournamentTeamRegistration {
  _id: string;
  name: string;
  shortName: string;
  logo: string;
  location: string;
  startDate: string;
  endDate: string;
  status: 'upcoming' | 'ongoing' | 'completed' | 'cancelled';
  sports: string;
  ageCategories: string[];
}

interface TournamentTeamRegistrationsTableProps {
  tournaments: TournamentTeamRegistration[];
  loading: boolean;
  currentPage: number;
  totalPages: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onViewTournament: (tournament: TournamentTeamRegistration) => void;
  onEditTournament: (tournament: TournamentTeamRegistration) => void;
  onDeleteTournament: (tournament: TournamentTeamRegistration) => void;
}

const TournamentTeamRegistrationsTable: React.FC<TournamentTeamRegistrationsTableProps> = ({
  tournaments,
  loading,
  currentPage,
  totalPages,
  totalCount,
  onPageChange,
  onViewTournament,
  onEditTournament,
  onDeleteTournament
}) => {
  const navigate = useNavigate();
  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'dd/MM/yyyy');
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const items = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // First page
    if (startPage > 1) {
      items.push(
        <Pagination.Item
          key={1}
          active={currentPage === 1}
          onClick={() => onPageChange(1)}
        >
          1
        </Pagination.Item>
      );
      if (startPage > 2) {
        items.push(<Pagination.Ellipsis key="ellipsis1" />);
      }
    }

    // Visible pages
    for (let page = startPage; page <= endPage; page++) {
      items.push(
        <Pagination.Item
          key={page}
          active={currentPage === page}
          onClick={() => onPageChange(page)}
        >
          {page}
        </Pagination.Item>
      );
    }

    // Last page
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        items.push(<Pagination.Ellipsis key="ellipsis2" />);
      }
      items.push(
        <Pagination.Item
          key={totalPages}
          active={currentPage === totalPages}
          onClick={() => onPageChange(totalPages)}
        >
          {totalPages}
        </Pagination.Item>
      );
    }

    return (
      <div className="d-flex justify-content-between align-items-center mt-3">
        <div className="text-muted">
          Showing {((currentPage - 1) * 10) + 1} to {Math.min(currentPage * 10, totalCount)} of {totalCount} tournament team registrations
        </div>
        <Pagination className="mb-0">
          <Pagination.First
            disabled={currentPage === 1}
            onClick={() => onPageChange(1)}
          />
          <Pagination.Prev
            disabled={currentPage === 1}
            onClick={() => onPageChange(currentPage - 1)}
          />
          {items}
          <Pagination.Next
            disabled={currentPage === totalPages}
            onClick={() => onPageChange(currentPage + 1)}
          />
          <Pagination.Last
            disabled={currentPage === totalPages}
            onClick={() => onPageChange(totalPages)}
          />
        </Pagination>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p className="mt-2 text-muted">Loading tournament team registrations...</p>
      </div>
    );
  }

  if (tournaments.length === 0) {
    return (
      <div className="text-center py-5">
        <div className="empty-state">
          <div className="empty-state-icon">
            <i className="ph-trophy"></i>
          </div>
          <div className="empty-state-title">No Tournament Team Registrations Found</div>
          <div className="empty-state-description">
            There are no tournament team registrations matching your criteria.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="tournament-team-registrations-table-container">
      <Table responsive className="clubs-table">
        <thead>
          <tr>
            <th>Tournament Name</th>
            <th>Sports</th>
            <th>Age Category</th>
            <th>Tournament Dates</th>
            <th>Venue</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {tournaments.map((tournament) => (
            <tr key={tournament._id}>
              <td>
                <div className="club-name-cell">
                  <span className="club-name">{tournament.name}</span>
                  <span className="location">{tournament.shortName}</span>
                </div>
              </td>
              <td>
                <span className="sports-text text-uppercase">
                  {tournament.sports}
                </span>
              </td>
              <td>
                <span className="age-categories-text">
                  {getAgeCategoriesDisplay(tournament.ageCategories)}
                </span>
              </td>
              <td>
                <span className="date-text">
                  {formatDate(tournament.startDate)}
                </span>
              </td>
              <td>
                <span className="venue-text">{tournament.location}</span>
              </td>
              <td>
                <div className="action-buttons">
                  <PermissionWrapper
                    anyOfPermissions={[
                      'tournament.admin',
                      'tournament.manage',
                      'tournament.registrations.manage',
                      'tournament.registration_details.manage',
                    ]}
                    hideWhenDisabled
                  >
                    <button
                      onClick={() => navigate(`/registered-tournaments/${tournament._id}`)}
                      className="btn-edit"
                      title="View Registration"
                    >
                      <i className="ri-pencil-line"></i>
                    </button>
                  </PermissionWrapper>
                  <PermissionWrapper
                    anyOfPermissions={[
                      'tournament.admin',
                      'tournament.manage',
                      'tournament.registrations.manage',
                      'tournament.registration_details.manage',
                    ]}
                    hideWhenDisabled
                  >
                    <button
                      onClick={() => onDeleteTournament(tournament)}
                      className="btn-delete"
                      title="Delete"
                    >
                      <i className="ri-delete-bin-line"></i>
                    </button>
                  </PermissionWrapper>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      
      {renderPagination()}
    </div>
  );
};

export default TournamentTeamRegistrationsTable; 